import React, { useState } from "react";
import { useHotel } from "@/hooks/useHotel";
import { useTranslation } from "react-i18next";
import {
  useGetPartnerLogs,
  useGetPartnerSettings,
  useUpdatePartnerSettings,
  useTriggerPartnerSync,
} from "./hooks/partner-settings.hooks";
import { BillingPageState } from "../billing-settings/components/BillingPageState";
import { PartnerSettingsCard } from "./components/PartnerSettingsCard";
import { PartnerSectionForm } from "./components/PartnerSectionForm";
import { UpdatePartnerSettingsRequest } from "@/types/partner-settings";
import { toast } from "sonner";
import {
  PartnerProviderLogsFilters,
  PartnerProviderLog,
} from "@/types/partner-provider-log";
import { PartnerProviderStatusCard } from "./components/PartnerProviderStatusCard";
import { PartnerProviderLogsTable } from "./components/PartnerProviderLogsTable";
import { partnerSettingsService } from "@/services/partner-settings-service";
import {
  buildPartnerProviderLogsExportFilename,
  downloadPartnerProviderLogsCsv,
  downloadPartnerProviderLogsExcel,
  mapPartnerProviderLogsForExport,
} from "./utils/exportPartnerProviderLogs";

export const PartnerIntegrationSettingsPage = (): React.JSX.Element => {
  const { hotelId } = useHotel();
  const { t } = useTranslation();
  const statusLogFilters: PartnerProviderLogsFilters = {
    page: 1,
    limit: 20,
  };
  const [logFilters, setLogFilters] = useState<PartnerProviderLogsFilters>({
    page: 1,
    limit: 20,
  });
  const [isExportingLogs, setIsExportingLogs] = useState(false);

  const { data: partnerSettings, isLoading, isError } = useGetPartnerSettings();
  const { data: statusLogsResponse, isLoading: isStatusLogsLoading } =
    useGetPartnerLogs(statusLogFilters);
  const { data: partnerLogsResponse, isLoading: isLogsLoading } =
    useGetPartnerLogs(logFilters);
  const updatePartnerSettings = useUpdatePartnerSettings();
  const triggerSync = useTriggerPartnerSync();

  if (!hotelId) {
    return <BillingPageState text={t("partners.hotelNotSelected")} />;
  }

  if (isLoading) {
    return <BillingPageState text={t("partners.loading")} />;
  }

  if (isError) {
    return <BillingPageState text={t("partners.loadingError")} tone="error" />;
  }

  if (!partnerSettings) {
    return <BillingPageState text={t("partners.noPartners")} />;
  }

  const handleSave = (data: UpdatePartnerSettingsRequest) => {
    updatePartnerSettings.mutate(data, {
      onSuccess: () => {
        toast.success(t("partners.saveSuccess"));
      },
      onError: () => {
        toast.error(t("partners.saveError"));
      },
    });
  };

  const fetchAllFilteredLogs = async (): Promise<PartnerProviderLog[]> => {
    const baseFilters: PartnerProviderLogsFilters = {
      ...logFilters,
      page: 1,
    };
    const firstPage = await partnerSettingsService.getPartnerLogs(
      hotelId,
      baseFilters,
    );
    const allLogs = [...firstPage.data];
    const totalPages = Math.max(
      1,
      Math.ceil(firstPage.meta.total / Math.max(firstPage.meta.limit, 1)),
    );

    if (totalPages <= 1) {
      return allLogs;
    }

    const remainingPages = Array.from(
      { length: totalPages - 1 },
      (_, index) => index + 2,
    );
    const pageResponses = await Promise.all(
      remainingPages.map((page) =>
        partnerSettingsService.getPartnerLogs(hotelId, {
          ...baseFilters,
          page,
        }),
      ),
    );

    pageResponses.forEach((response) => {
      allLogs.push(...response.data);
    });

    return allLogs;
  };

  const handleLogsExport = async (formatType: "csv" | "excel") => {
    try {
      setIsExportingLogs(true);

      const logs = await fetchAllFilteredLogs();

      if (logs.length === 0) {
        toast.error(t("partners.admin.export.noData"));
        return;
      }

      const rows = mapPartnerProviderLogsForExport(logs, {
        getEventLabel: (value) =>
          t(`partners.admin.eventTypeValues.${value}`, { defaultValue: value }),
        getStatusLabel: (value) =>
          t(`partners.admin.logStatusValues.${value}`, { defaultValue: value }),
        getLevelLabel: (value) =>
          t(`partners.admin.logLevelValues.${value}`, { defaultValue: value }),
      });

      if (formatType === "csv") {
        downloadPartnerProviderLogsCsv(
          rows,
          buildPartnerProviderLogsExportFilename("csv"),
        );
      } else {
        downloadPartnerProviderLogsExcel(
          rows,
          buildPartnerProviderLogsExportFilename("xls"),
        );
      }

      toast.success(
        t("partners.admin.export.success", {
          count: logs.length,
          format:
            formatType === "csv"
              ? t("partners.admin.export.csv")
              : t("partners.admin.export.excel"),
        }),
      );
    } catch (error) {
      const description =
        error instanceof Error
          ? error.message
          : t("partners.admin.export.error");

      toast.error(description);
    } finally {
      setIsExportingLogs(false);
    }
  };

  return (
    <div className="flex max-w-6xl flex-col gap-6 p-6 md:p-12">
      <PartnerSettingsCard>
        <PartnerSectionForm
          settings={partnerSettings}
          isSaving={updatePartnerSettings.isPending}
          isSyncing={triggerSync.isPending}
          onSave={handleSave}
          onSync={() => {
            triggerSync.mutate(undefined, {
              onSuccess: () => {
                toast.success(t("partners.syncSuccess"));
                setLogFilters((prev) => ({
                  ...prev,
                  page: 1,
                }));
              },
              onError: () => {
                toast.error(t("partners.syncErrorMessage"));
              },
            });
          }}
        />
      </PartnerSettingsCard>

      <PartnerProviderStatusCard
        logs={statusLogsResponse?.data ?? []}
        lastSyncAt={partnerSettings.lastSyncAt}
        isLoading={isStatusLogsLoading}
      />

      <PartnerProviderLogsTable
        isLoading={isLogsLoading}
        isExporting={isExportingLogs}
        logs={partnerLogsResponse?.data ?? []}
        meta={partnerLogsResponse?.meta ?? null}
        filters={logFilters}
        onFiltersChange={setLogFilters}
        onExport={handleLogsExport}
      />
    </div>
  );
};
